import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CONTACT_INFO, SERVICES } from '../data';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const currentPath = location.pathname;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [currentPath]);

  const isActive = (path) => {
    if (path === '/') {
      return currentPath === '/';
    }
    return currentPath.startsWith(path);
  };

  const activeClass = "bg-white text-primary-green font-bold px-4 py-3 rounded-sm shadow-xs";
  const inactiveClass = "text-white hover:bg-primary-green-light hover:text-white px-4 py-3 rounded-sm transition-all duration-200";

  return (
    <header className="sticky top-0 z-50 w-full bg-white shadow-md">
      {/* Top Utility Bar */}
      <div className="border-b border-gray-100 bg-neutral-light-bg py-2.5 text-xs text-neutral-muted">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="hidden items-center space-x-6 lg:flex">
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-clock text-secondary-green" />
              <span>{CONTACT_INFO.hours}</span>
            </span>
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-location-dot text-secondary-green" />
              <span>{CONTACT_INFO.address}</span>
            </span>
          </div>
          <div className="flex w-full items-center justify-between md:w-auto md:justify-end md:space-x-6">
            <a 
              href={`tel:${CONTACT_INFO.phoneRaw}`} 
              className="flex items-center gap-2 font-semibold text-primary-green hover:text-secondary-green transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-secondary-green" />
              <span className="whitespace-nowrap">{CONTACT_INFO.phone}</span>
            </a>
            <a 
              href={`mailto:${CONTACT_INFO.email}`} 
              className="hidden items-center gap-2 hover:text-primary-green transition-colors sm:flex whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-secondary-green" />
              <span className="whitespace-nowrap">{CONTACT_INFO.email}</span>
            </a>
            <Link 
              to="/kontakt" 
              className="ml-4 bg-accent-orange hover:bg-accent-orange-dark text-white font-display text-[11px] font-bold px-4 py-1.5 uppercase tracking-wider transition-all duration-200 rounded-xs shadow-xs"
            >
              Få et tilbud
            </Link>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="bg-primary-green py-3 relative">
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-10 flex justify-between items-center px-12 overflow-hidden">
          <i className="fa-solid fa-leaf text-white text-5xl animate-float-leaf-1 transform translate-y-2" />
          <i className="fa-solid fa-leaf text-white text-4xl animate-float-leaf-2 transform -translate-y-2 hidden md:block" />
          <i className="fa-solid fa-leaf text-white text-6xl animate-float-leaf-3 transform translate-y-1 hidden lg:block" />
        </div>

        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://assets.auxodev.dk/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/logo-4.png" 
              alt="Haveglæde Logo" 
              className="h-12 w-auto object-contain bg-white p-1 rounded-sm"
              onError={(e) => {
                e.target.src = "/images/auxo_logo_white.png";
              }}
            />
          </Link>

          {/* Desktop Navigation (Visible >= 1200px) */}
          <nav className="hidden xl:flex items-center space-x-1 text-xs font-display font-bold uppercase tracking-wider">
            <Link to="/" className={isActive('/') ? activeClass : inactiveClass}>
              Forside
            </Link>
            <Link to="/om-haveglaede" className={isActive('/om-haveglaede') ? activeClass : inactiveClass}>
              Om Haveglæde
            </Link>
            
            {/* Dropdown for Ydelser */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`${isActive('/ydelser') ? activeClass : inactiveClass} flex items-center gap-1.5 cursor-pointer`}
                aria-expanded={isDropdownOpen}
              >
                Ydelser
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isDropdownOpen && (
                <div className="absolute left-0 mt-2 w-56 rounded-sm bg-white py-1 shadow-lg ring-1 ring-black/5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                  {SERVICES.map((service) => (
                    <Link
                      key={service.slug}
                      to={`/ydelser/${service.slug}`}
                      onClick={() => setIsDropdownOpen(false)}
                      className="block px-4 py-2.5 text-xs text-neutral-dark hover:bg-neutral-light-bg hover:text-primary-green transition-colors font-semibold"
                    >
                      <i className={`${service.icon} w-5 text-secondary-green mr-2`} />
                      {service.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/referencer" className={isActive('/referencer') ? activeClass : inactiveClass}>
              Referencer
            </Link>
            <Link to="/kontakt" className={isActive('/kontakt') ? activeClass : inactiveClass}>
              Kontakt
            </Link>
          </nav>

          {/* Mobile Menu Button (Visible < 1200px) */}
          <div className="flex xl:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-white hover:bg-primary-green-light focus:outline-none cursor-pointer"
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
            >
              <span className="sr-only">Åbn menu</span>
              {isOpen ? (
                <i className="fa-solid fa-xmark text-2xl" />
              ) : (
                <i className="fa-solid fa-bars text-2xl" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu (Visible < 1200px) */}
      {isOpen && (
        <div className="xl:hidden bg-primary-green border-t border-primary-green-light animate-in slide-in-from-top duration-200" id="mobile-menu">
          <div className="space-y-1 px-4 py-4 text-sm font-display font-bold uppercase tracking-wider text-white">
            <Link 
              to="/" 
              className={`block px-3 py-2.5 rounded-md ${isActive('/') ? 'bg-white text-primary-green' : 'hover:bg-primary-green-light'}`}
            >
              Forside
            </Link>
            <Link 
              to="/om-haveglaede" 
              className={`block px-3 py-2.5 rounded-md ${isActive('/om-haveglaede') ? 'bg-white text-primary-green' : 'hover:bg-primary-green-light'}`}
            >
              Om Haveglæde
            </Link>
            
            {/* Mobile Ydelser Section */}
            <div className="border-t border-primary-green-light/30 my-2 pt-2">
              <span className="block px-3 py-1 text-xs font-semibold text-secondary-green tracking-widest uppercase">
                Ydelser
              </span>
              <div className="pl-4 mt-1 space-y-1">
                {SERVICES.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/ydelser/${service.slug}`}
                    className={`block px-3 py-2 rounded-md text-xs ${isActive(`/ydelser/${service.slug}`) ? 'bg-white/10 text-secondary-green' : 'hover:bg-primary-green-light'}`}
                  >
                    <i className={`${service.icon} w-5 text-secondary-green mr-2`} />
                    {service.title}
                  </Link>
                ))}
              </div>
            </div>

            <Link 
              to="/referencer" 
              className={`block px-3 py-2.5 rounded-md ${isActive('/referencer') ? 'bg-white text-primary-green' : 'hover:bg-primary-green-light'}`}
            >
              Referencer
            </Link>
            <Link 
              to="/kontakt" 
              className={`block px-3 py-2.5 rounded-md ${isActive('/kontakt') ? 'bg-white text-primary-green' : 'hover:bg-primary-green-light'}`}
            >
              Kontakt
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
